import React from "react";
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { CostForwardingPut } from "models/utilTypes/costForwardingTypes";
import {
  CostForwardingFormValues,
  getCostForwardingFormValues,
  getCostForwardingValuesSaved,
  getCostForwardingReadId,
  isCostForwardingOffered,
  isForwardableReceipt,
} from "./costForwardingMarkingModel";
//#endregion
//#region components
import useInvoicePermissions from "components/invoice/useInvoicePemissions";
import useCostForwardingMarking from "./useCostForwardingMarking";
import { getCostForwardingSavedEventName } from "./useSaveCostForwardingMarking";
//#endregion
import { EventHelper } from "utils/helpers";

/** Loads a receipt to-do's marking for its CostForwardingMarkingProvider and its form's default values. */
function useReceiptTodoCostForwarding(
  receipt: WintSuperkollAPIModel.Receipt,
  getFormValues?: () => CostForwardingFormValues | undefined
) {
  // Without invoicing rights the marking is not even requested, so the step never flashes up.
  const { canUseInvoices } = useInvoicePermissions();
  const loadedCostForwarding = useCostForwardingMarking(
    "receipt",
    getCostForwardingReadId(receipt.id, canUseInvoices),
    receipt
  );
  const isForwardable = isForwardableReceipt(receipt);
  const costForwarding = React.useMemo(
    () => ({
      ...loadedCostForwarding,
      isAvailable: isCostForwardingOffered(!!canUseInvoices, loadedCostForwarding.isAvailable, isForwardable),
    }),
    [loadedCostForwarding, canUseInvoices, isForwardable]
  );

  // Read at the moment the receipt itself reinitialises the form. A marking that arrives or changes
  // later is patched in by the form sync, so it never resets the user's other edits.
  const costForwardingValues = getCostForwardingFormValues(
    costForwarding.marking,
    costForwarding.isAvailable,
    receipt.supplierName
  );
  // "Spara utkast" reloads the receipt before the marking is read again, so until then the form keeps what it saved.
  const savedValues = React.useRef<CostForwardingFormValues>();
  React.useEffect(() => {
    if (!getFormValues) return;

    const eventName = getCostForwardingSavedEventName("receipt", receipt.id);
    const onSaved = (event: CustomEvent<CostForwardingPut>) => {
      const values = getFormValues();
      savedValues.current = values ? getCostForwardingValuesSaved(values, event.detail) : undefined;
    };

    EventHelper.subscribeEvent(eventName, onSaved);
    return () => EventHelper.unsubscribeEvent(eventName, onSaved);
  }, [receipt.id]);

  React.useEffect(() => {
    savedValues.current = undefined;
  }, [loadedCostForwarding.marking]);

  const latestCostForwardingValues = React.useRef(costForwardingValues);
  latestCostForwardingValues.current = savedValues.current ?? costForwardingValues;

  const costForwardingContext = React.useMemo(
    () => ({ ...costForwarding, source: "receipt" as const, sourceId: receipt.id, supplierName: receipt.supplierName }),
    [costForwarding, receipt.id, receipt.supplierName]
  );

  return { costForwardingContext, latestCostForwardingValues };
}

export default useReceiptTodoCostForwarding;
